// src/pages/SupportPage.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Mail,
  MessageCircle,
  ChevronDown,
  BookOpen,
  CreditCard,
  Mic,
  Settings,
  Sparkles,
  Search,
  RotateCcw,
  Wifi,
  RefreshCw,
  ImageIcon,
} from "lucide-react";

/* ============================================================
   Replace the logo:
   Put your file at  public/logo.png  (or update LOGO_SRC below)
   Recommended: square or wide transparent PNG/SVG, ~200px tall.
   Until a real file is found, a placeholder slot is shown instead.
   ============================================================ */
const LOGO_SRC = "/logo.png";

const SUPPORT_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@300;400;500;700;800&display=swap');

  :root {
    --teal-950: #04302c;
    --teal-900: #0a4a42;
    --teal-800: #0d5e54;
    --teal-700: #0f7d6f;
    --teal-600: #129c8a;
    --teal-500: #17b6a0;
    --teal-300: #6fe0cd;
    --teal-100: #d7f9f0;
    --teal-50:  #f1fdfa;
    --ink:      #073b34;
    --ink-soft: #3d6b64;
    --paper:    #ffffff;
    --line:     rgba(7,59,52,0.12);
  }

  .support-root {
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
    background: var(--teal-50);
    font-family: 'Tajawal', sans-serif;
    color: var(--ink);
    direction: rtl;
  }

  .support-root.dark-mode {
    background: var(--teal-950);
    color: var(--teal-100);
    --ink: #eafff9;
    --ink-soft: #9fd6ca;
    --paper: #0a4a42;
    --line: rgba(215,249,240,0.12);
  }

  * { box-sizing: border-box; }

  .support-shell {
    position: relative; z-index: 1;
    max-width: 880px; margin: 0 auto;
    padding: 32px 20px 90px;
  }

  /* ── Top bar: logo + live status ── */
  .support-topbar {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 28px;
    gap: 16px;
  }

  .support-brand {
    display: flex; align-items: center; gap: 12px;
  }

  .support-logo-slot {
    width: 44px; height: 44px; border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    overflow: hidden; flex-shrink: 0;
    background: var(--paper);
    border: 1px solid var(--line);
  }
  .support-logo-slot img { width: 100%; height: 100%; object-fit: contain; }
  .support-logo-slot.empty {
    border: 1.5px dashed var(--teal-500);
    background: var(--teal-100);
    color: var(--teal-700);
  }
  .dark-mode .support-logo-slot.empty {
    background: rgba(111,224,205,0.08);
    color: var(--teal-300);
  }

  .support-brand-name {
    font-size: 15px; font-weight: 700; line-height: 1.2;
  }
  .support-brand-sub {
    font-size: 12px; color: var(--ink-soft); font-weight: 500;
  }

  .support-status-pill {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 7px 14px; border-radius: 99px;
    background: var(--paper);
    border: 1px solid var(--line);
    font-size: 12.5px; font-weight: 600; color: var(--ink-soft);
    flex-shrink: 0;
  }
  .support-status-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: var(--teal-500);
    position: relative; flex-shrink: 0;
  }
  .support-status-dot::after {
    content: ''; position: absolute; inset: -4px;
    border-radius: 50%; border: 1.5px solid var(--teal-500);
    animation: statusPulse 2.2s ease-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .support-status-dot::after { animation: none; }
  }
  @keyframes statusPulse {
    0% { transform: scale(0.6); opacity: 0.9; }
    100% { transform: scale(1.9); opacity: 0; }
  }

  /* ── Hero ── */
  .support-hero {
    position: relative;
    background: linear-gradient(155deg, var(--teal-700) 0%, var(--teal-600) 55%, var(--teal-500) 100%);
    border-radius: 24px;
    padding: 40px 32px 0;
    overflow: hidden;
    margin-bottom: 24px;
    opacity: 0;
    animation: heroRise 0.6s ease-out forwards;
  }
  @keyframes heroRise {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @media (prefers-reduced-motion: reduce) {
    .support-hero { opacity: 1; animation: none; }
  }

  .support-hero-inner { position: relative; z-index: 2; padding-bottom: 34px; }

  .support-title {
    font-size: 27px; font-weight: 800; line-height: 1.35;
    color: #ffffff; margin-bottom: 10px; max-width: 480px;
  }
  .support-subtitle {
    font-size: 14.5px; color: rgba(255,255,255,0.88); line-height: 1.85;
    max-width: 460px; font-weight: 400;
  }

  .support-wave {
    position: absolute; bottom: -2px; left: 0; width: 200%; height: 46px;
    animation: waveDrift 16s linear infinite;
  }
  @media (prefers-reduced-motion: reduce) { .support-wave { animation: none; } }
  @keyframes waveDrift {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }

  /* ── Stats ── */
  .support-stats {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 1px;
    background: var(--line);
    border: 1px solid var(--line);
    border-radius: 18px;
    overflow: hidden;
    margin-bottom: 24px;
  }
  .support-stat {
    background: var(--paper);
    padding: 20px 14px;
    text-align: center;
  }
  .support-stat-num {
    font-size: 24px; font-weight: 800; color: var(--teal-700);
    line-height: 1.1;
  }
  .dark-mode .support-stat-num { color: var(--teal-300); }
  .support-stat-label {
    font-size: 12px; color: var(--ink-soft); margin-top: 5px; font-weight: 500;
  }

  /* ── Contact cards ── */
  .support-contact-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
    margin-bottom: 30px;
  }
  @media (max-width: 600px) { .support-contact-grid { grid-template-columns: 1fr; } }

  .support-contact-card {
    background: var(--paper);
    border: 1px solid var(--line);
    border-radius: 18px;
    padding: 22px 20px;
    text-decoration: none;
    display: block;
    color: inherit;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }
  .support-contact-card:hover {
    border-color: var(--teal-500);
    box-shadow: 0 6px 22px rgba(15,125,111,0.14);
  }

  .support-card-icon-wrap {
    width: 40px; height: 40px; border-radius: 11px;
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 14px;
    background: var(--teal-100);
    color: var(--teal-700);
  }
  .dark-mode .support-card-icon-wrap {
    background: rgba(111,224,205,0.1);
    color: var(--teal-300);
  }

  .support-card-title { font-size: 15.5px; font-weight: 700; margin-bottom: 4px; }
  .support-card-value {
    font-size: 13px; color: var(--ink-soft);
    margin-bottom: 14px; direction: ltr; text-align: right; font-weight: 500;
  }
  .support-card-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 8px 16px; border-radius: 10px;
    font-size: 13px; font-weight: 700;
    background: var(--teal-700); color: #fff;
    transition: background 0.15s ease;
  }
  .support-contact-card:hover .support-card-btn { background: var(--teal-600); }

  /* ── Self-service steps ── */
  .support-section-heading {
    font-size: 16px; font-weight: 700; margin-bottom: 14px;
  }

  .support-steps {
    position: relative;
    margin-bottom: 30px;
    padding-inline-start: 4px;
  }
  .support-step {
    display: flex; gap: 14px; position: relative;
    padding-bottom: 18px;
  }
  .support-step:last-child { padding-bottom: 0; }
  .support-step::before {
    content: ''; position: absolute;
    right: 15px; top: 32px; bottom: 0; width: 1.5px;
    background: var(--line);
  }
  .support-step:last-child::before { display: none; }
  .support-step-num {
    width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
    background: var(--teal-100); color: var(--teal-700);
    display: flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 800; z-index: 1;
  }
  .dark-mode .support-step-num { background: rgba(111,224,205,0.1); color: var(--teal-300); }
  .support-step-body { padding-top: 5px; }
  .support-step-title { font-size: 14px; font-weight: 700; margin-bottom: 3px; }
  .support-step-text { font-size: 13px; color: var(--ink-soft); line-height: 1.7; }

  /* ── FAQ search ── */
  .support-search {
    position: relative; margin-bottom: 16px;
  }
  .support-search input {
    width: 100%; padding: 13px 44px 13px 16px;
    border-radius: 13px; border: 1px solid var(--line);
    background: var(--paper); color: var(--ink);
    font-family: 'Tajawal', sans-serif; font-size: 14px; font-weight: 500;
    outline: none; transition: border-color 0.15s ease;
  }
  .support-search input:focus { border-color: var(--teal-500); }
  .support-search input::placeholder { color: var(--ink-soft); opacity: 0.7; }
  .support-search-icon {
    position: absolute; right: 15px; top: 50%; transform: translateY(-50%);
    color: var(--ink-soft); pointer-events: none;
  }

  .faq-item {
    background: var(--paper);
    border: 1px solid var(--line);
    border-radius: 14px;
    margin-bottom: 9px;
    overflow: hidden;
  }

  .faq-question {
    display: flex; align-items: center; justify-content: space-between;
    padding: 15px 18px; cursor: pointer; gap: 12px;
    user-select: none;
  }
  .faq-question-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
  .faq-icon {
    width: 30px; height: 30px; border-radius: 9px; flex-shrink: 0;
    background: var(--teal-100); color: var(--teal-700);
    display: flex; align-items: center; justify-content: center;
  }
  .dark-mode .faq-icon { background: rgba(111,224,205,0.1); color: var(--teal-300); }
  .faq-question-text { font-size: 13.5px; font-weight: 600; line-height: 1.4; }

  .faq-chevron { color: var(--ink-soft); flex-shrink: 0; transition: transform 0.2s ease; }
  .faq-chevron.open { transform: rotate(180deg); }

  .faq-answer-wrap {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.25s ease;
  }
  .faq-answer-wrap.open { grid-template-rows: 1fr; }
  .faq-answer-inner { overflow: hidden; }
  .faq-answer {
    padding: 0 18px 16px 18px;
    font-size: 13.5px; line-height: 1.9; color: var(--ink-soft);
    padding-right: 60px;
  }

  .support-empty {
    text-align: center; padding: 30px 16px;
    color: var(--ink-soft); font-size: 13.5px;
  }
  .support-empty-reset {
    display: inline-flex; align-items: center; gap: 6px;
    margin-top: 10px; color: var(--teal-700); font-weight: 700;
    font-size: 13px; cursor: pointer; background: none; border: none;
    font-family: 'Tajawal', sans-serif;
  }
  .dark-mode .support-empty-reset { color: var(--teal-300); }

  .support-footer-note {
    text-align: center; font-size: 12px; color: var(--ink-soft);
    margin-top: 30px; line-height: 1.8;
  }

  @media (max-width: 640px) {
    .support-title { font-size: 21px; }
    .support-hero { padding: 30px 22px 0; }
    .faq-answer { padding-right: 18px; }
    .support-stats { grid-template-columns: 1fr; }
  }
`;

const faqs = [
  {
    icon: BookOpen,
    question: "كيف أبدأ دورة جديدة؟",
    answer:
      "بعد تسجيل الدخول، انتقل إلى صفحة الدورات من القائمة الرئيسية، واختر الدورة التي تناسبك. تحتوي كل دورة على مستويات تبدأ من المبتدئ وتصل إلى المتقدم، ويمكنك البدء من أي مستوى تختاره.",
  },
  {
    icon: CreditCard,
    question: "ما طرق الدفع المتاحة؟",
    answer:
      "نقبل البطاقات الائتمانية، وبطاقات مدى، وبعض خدمات الدفع الإلكتروني. وإذا واجهت أي مشكلة في الدفع، فتواصل معنا وسنساعدك في أقرب وقت ممكن.",
  },
  {
    icon: Mic,
    question: "لا تعمل ميزة التحدث لديّ، فماذا أفعل؟",
    answer:
      "تأكد أولًا من أن التطبيق يملك صلاحية الوصول إلى الميكروفون من إعدادات جهازك. وإذا استمرت المشكلة، فحاول إغلاق التطبيق وإعادة فتحه. وإذا لم تُحل المشكلة، فتواصل معنا وأرسل لنا نوع جهازك وإصدار التطبيق.",
  },
  {
    icon: Settings,
    question: "كيف أعدّل معلومات حسابي؟",
    answer:
      "من الصفحة الرئيسية، اضغط على أيقونة الملف الشخصي في الأعلى، ثم اختر إعدادات الحساب. من هناك يمكنك تغيير اسمك وبريدك الإلكتروني وكلمة المرور.",
  },
  {
    icon: Sparkles,
    question: "ما الفرق بين اختبار IELTS التجريبي واختبار STEP؟",
    answer:
      "اختبار IELTS التجريبي مصمم للمتعلمين الذين يستعدون للاختبار الدولي، ويغطي المهارات الأربع: الاستماع، والقراءة، والكتابة، والتحدث. أما اختبار STEP فهو الاختبار السعودي لقياس الكفاءة في اللغة الإنجليزية، وهو معتمد لدى العديد من الجامعات والجهات الحكومية.",
  },
  {
    icon: BookOpen,
    question: "هل يمكنني الوصول إلى التطبيق من أكثر من جهاز؟",
    answer:
      "نعم، حسابك مرتبط ببريدك الإلكتروني، ويمكنك تسجيل الدخول من أي جهاز. ويتم مزامنة تقدّمك تلقائيًا بين جميع الأجهزة.",
  },
];

const steps = [
  {
    title: "أغلق التطبيق ثم أعد فتحه",
    text: "يحل ذلك كثيرًا من المشكلات البسيطة الناتجة عن تعليق مؤقت في التطبيق.",
  },
  {
    title: "تأكد من اتصال الإنترنت",
    text: "حاول فتح موقع آخر للتأكد من أن الاتصال يعمل بشكل طبيعي.",
  },
  {
    title: "حدّث التطبيق إلى آخر إصدار",
    text: "تعمل الإصدارات الجديدة على إصلاح مشكلات معروفة وتحسين الأداء العام.",
  },
  {
    title: "راجع صلاحية الميكروفون",
    text: "إذا كانت المشكلة في ميزة التحدث، فتأكد من منح التطبيق إذن الوصول إلى الميكروفون من إعدادات جهازك.",
  },
];

function LogoSlot() {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="support-logo-slot empty" title="logo.png">
        <ImageIcon size={18} strokeWidth={2} />
      </div>
    );
  }
  return (
    <div className="support-logo-slot">
      <img src={LOGO_SRC} alt="شعار Lexera" onError={() => setFailed(true)} />
    </div>
  );
}

export default function SupportPage({ darkMode = false }) {
  const [openFaq, setOpenFaq] = useState(null);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    const id = "support-page-styles";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style");
      tag.id = id;
      tag.textContent = SUPPORT_STYLES;
      document.head.appendChild(tag);
    }
  }, []);

  const filteredFaqs = useMemo(() => {
    const q = query.trim();
    if (!q) return faqs;
    return faqs.filter(
      (f) => f.question.includes(q) || f.answer.includes(q)
    );
  }, [query]);

  return (
    <div className={`support-root ${darkMode ? "dark-mode" : ""}`}>
      <div className="support-shell">
        {/* Top bar */}
        <div className="support-topbar">
          <div className="support-brand">
            <LogoSlot />
            <div>
              <div className="support-brand-name">Lexera</div>
              <div className="support-brand-sub">مركز الدعم</div>
            </div>
          </div>
          <div className="support-status-pill">
            <span className="support-status-dot" />
            الفريق متصل الآن
          </div>
        </div>

        {/* Hero */}
        <div className="support-hero">
          <div className="support-hero-inner">
            <h1 className="support-title">كيف يمكننا مساعدتك اليوم؟</h1>
            <p className="support-subtitle">
              هل واجهت مشكلة أو لديك سؤال؟ فريق Lexera جاهز لمساعدتك عبر
              البريد الإلكتروني أو واتساب، من السبت إلى الخميس، من الساعة
              التاسعة صباحًا حتى العاشرة مساءً بتوقيت السعودية.
            </p>
          </div>
          <svg
            className="support-wave"
            viewBox="0 0 800 46"
            preserveAspectRatio="none"
          >
            <path
              d="M0,20 C100,45 200,0 300,18 C400,36 500,4 600,20 C700,36 750,10 800,20 L800,46 L0,46 Z M800,20 C900,45 1000,0 1100,18 C1200,36 1300,4 1400,20 C1500,36 1550,10 1600,20 L1600,46 L800,46 Z"
              fill="var(--teal-50)"
              className="dark-mode-wave"
            />
          </svg>
        </div>

        {/* Stats */}
        <div className="support-stats">
          <div className="support-stat">
            <div className="support-stat-num">٢٤ساعة</div>
            <div className="support-stat-label">متوسط وقت الرد</div>
          </div>
          <div className="support-stat">
            <div className="support-stat-num">٦ أيام</div>
            <div className="support-stat-label">متاحون أسبوعيًا</div>
          </div>
          <div className="support-stat">
            <div className="support-stat-num">واتساب</div>
            <div className="support-stat-label">أسرع للطوارئ</div>
          </div>
        </div>

        {/* Contact Cards */}
        <div className="support-contact-grid">
          <a className="support-contact-card" href="mailto:support@lexera.app?subject=استفسار دعم فني&body=مرحباً، أحتاج مساعدة في...">
            <div className="support-card-icon-wrap">
              <Mail size={20} />
            </div>
            <div className="support-card-title">راسلنا عبر البريد الإلكتروني</div>
            <div className="support-card-value">support@lexera.app</div>
            <div className="support-card-btn">
              <Mail size={14} />
              إرسال بريد إلكتروني
            </div>
          </a>

          <a
            className="support-contact-card"
            href="https://wa.me/+966567022102"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="support-card-icon-wrap">
              <MessageCircle size={20} />
            </div>
            <div className="support-card-title">تواصل على واتساب</div>
            <div className="support-card-value" dir="ltr">
              +966567022102
            </div>
            <div className="support-card-btn">
              <MessageCircle size={14} />
              بدء محادثة
            </div>
          </a>
        </div>

        {/* Self-service steps */}
        <div className="support-section-heading">جرّب هذه الخطوات أولًا</div>
        <div className="support-steps">
          {steps.map((s, i) => (
            <div className="support-step" key={i}>
              <div className="support-step-num">{i + 1}</div>
              <div className="support-step-body">
                <div className="support-step-title">{s.title}</div>
                <div className="support-step-text">{s.text}</div>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="support-section-heading">الأسئلة الشائعة</div>

        <div className="support-search">
          <input
            ref={inputRef}
            type="text"
            placeholder="ابحث عن سؤالك..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Search size={17} className="support-search-icon" />
        </div>

        {filteredFaqs.length === 0 ? (
          <div className="support-empty">
            <RefreshCw size={20} style={{ marginBottom: 8, color: "var(--ink-soft)" }} />
            <div>لا توجد أسئلة مطابقة لبحثك.</div>
            <button className="support-empty-reset" onClick={() => setQuery("")}>
              <RotateCcw size={13} />
              مسح البحث
            </button>
          </div>
        ) : (
          filteredFaqs.map((faq) => {
            const Icon = faq.icon;
            const key = faq.question;
            const open = openFaq === key;
            return (
              <div className="faq-item" key={key}>
                <div
                  className="faq-question"
                  onClick={() => setOpenFaq(open ? null : key)}
                >
                  <div className="faq-question-left">
                    <div className="faq-icon">
                      <Icon size={14} />
                    </div>
                    <div className="faq-question-text">{faq.question}</div>
                  </div>
                  <div className={`faq-chevron ${open ? "open" : ""}`}>
                    <ChevronDown size={17} />
                  </div>
                </div>
                <div className={`faq-answer-wrap ${open ? "open" : ""}`}>
                  <div className="faq-answer-inner">
                    <div className="faq-answer">{faq.answer}</div>
                  </div>
                </div>
              </div>
            );
          })
        )}

        <p className="support-footer-note">
          <Wifi size={12} style={{ verticalAlign: "-1px", marginLeft: 4 }} />
          إذا لم تجد إجابة لسؤالك هنا، فتواصل معنا مباشرة وسنكون سعداء
          بمساعدتك.
          <br />
          Lexera — نتعلّم معًا
        </p>
      </div>
    </div>
  );
}